import { reactive, ref, watch } from 'vue'
import { fetchDroneInfo, updateDroneInfo } from '@/api/droneInfoApi'
import type { DroneInfo, DroneInfoDisp } from '@/types/droneInfo'
import { useToast } from '@/composables/useToast'
import { getFieldMessage } from '@/utils/message'

export function useDroneInfoLogic() {
  const initialDroneInfo: DroneInfoDisp = {
    drone_name: '',
    wind_caution: null,
    wind_warning: null,
    rain_caution: null,
    rain_warning: null,
    snow_caution: null,
    snow_warning: null,
    heat_caution: null,
    heat_warning: null,
    cold_caution: null,
    cold_warning: null,
    comment: '',
  }

  // 状態
  const droneInfo = reactive<DroneInfoDisp>({ ...initialDroneInfo })
  const isSaving = ref(false)
  const { show } = useToast()

  const originalSnapshot = ref<string>('')
  const isDirty = ref(false)

  function setSnapshot() {
    try {
      originalSnapshot.value = JSON.stringify(droneInfo)
      isDirty.value = false
    } catch {
      originalSnapshot.value = ''
      isDirty.value = false
    }
  }

  function isChanged(): boolean {
    try {
      return JSON.stringify(droneInfo) !== originalSnapshot.value
    } catch {
      return true
    }
  }

  // droneInfo を監視して isDirty を自動更新
  watch(
    () => droneInfo,
    () => {
      isDirty.value = isChanged()
    },
    { deep: true },
  )

  // 取得
  const loadDroneInfo = async () => {
    try {
      const data = (await fetchDroneInfo()) as DroneInfo | null
      if (data) {
        Object.assign(droneInfo, data)
        formatDecimal1(data)
        formatDecimal(data)
      }
      setSnapshot()
    } catch {
      Object.assign(droneInfo, initialDroneInfo)
      setSnapshot()
    }
  }

  // クリア
  const clearForm = () => {
    Object.assign(droneInfo, initialDroneInfo)
  }

  // 空文字や null を許容して数値にする
  function toNumberOrNull(v: unknown): number | null {
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }

  // 表示用オブジェクトから API 更新用の droneInfo を作る
  function setDroneInfoForUpdate(): DroneInfo {
    return {
      drone_name: droneInfo.drone_name ?? '',
      wind_caution: toNumberOrNull(droneInfo.wind_caution),
      wind_warning: toNumberOrNull(droneInfo.wind_warning),
      rain_caution: toNumberOrNull(droneInfo.rain_caution),
      rain_warning: toNumberOrNull(droneInfo.rain_warning),
      snow_caution: toNumberOrNull(droneInfo.snow_caution),
      snow_warning: toNumberOrNull(droneInfo.snow_warning),
      heat_caution: toNumberOrNull(droneInfo.heat_caution),
      heat_warning: toNumberOrNull(droneInfo.heat_warning),
      cold_caution: toNumberOrNull(droneInfo.cold_caution),
      cold_warning: toNumberOrNull(droneInfo.cold_warning),
      comment: droneInfo.comment ?? null,
    }
  }

  // 更新
  async function update() {
    if (isSaving.value) return
    isSaving.value = true
    try {
      const droneInfoForUpdate = setDroneInfoForUpdate()
      await updateDroneInfo(droneInfoForUpdate)
      await loadDroneInfo()
      show(getFieldMessage('operationncompleted004'))
      setSnapshot()
    } finally {
      isSaving.value = false
    }
  }

  // 値を少数固定文字列に変換（失敗時は null）
  function formatToFixedString(value: unknown, decimals = 0): string | null {
    if (value == null || value === '') return null
    const n = Number(value)
    if (Number.isNaN(n)) return null
    return n.toFixed(decimals) // 常に "8.0" のような文字列を返す
  }

  // 少数１桁項目をフォーマットする
  function formatDecimal1(data: DroneInfo) {
    const keys = [
      'wind_caution',
      'wind_warning',
      'rain_caution',
      'rain_warning',
      'heat_caution',
      'heat_warning',
      'cold_caution',
      'cold_warning',
    ] as const

    for (const k of keys) {
      if (k in data) {
        const formatted = formatToFixedString(data[k], 1)
        droneInfo[k as keyof DroneInfo] = formatted ?? ''
      }
    }
  }

  // 少数なしの数値項目を文字列に変換する
  function formatDecimal(data: DroneInfo) {
    const keys = ['snow_caution', 'snow_warning'] as const

    for (const k of keys) {
      if (k in data) {
        const formatted = formatToFixedString(data[k])
        droneInfo[k as keyof DroneInfo] = formatted ?? ''
      }
    }
  }

  return {
    droneInfo,
    loadDroneInfo,
    clearForm,
    isDirty,
    setSnapshot,
    isSaving,
    update,
  }
}
